import { listMaintenanceTasks } from '@/modules/maintenance/services/maintenanceRepository';
import type { MaintenanceTask } from '@/modules/maintenance/types/maintenance';
import { listReservationsInRange } from '@/modules/reservations/services/reservationRepository';
import { isBlockingReservation } from '@/modules/reservations/utils/reservationFilters';
import { isDateRangeOverlapping } from '@/modules/maintenance/utils/dateOverlap';
import { isBlockingMaintenanceStatus } from '@/modules/maintenance/utils/maintenanceStatusMachine';

export type MaintenanceDashboardStats = {
  itemsInMaintenance: number;
  activeRepairs: number;
  pendingInspections: number;
  maintenanceReservationConflicts: number;
};

function countByTypeAndStatus(
  tasks: MaintenanceTask[],
  type: MaintenanceTask['type'],
  statuses: MaintenanceTask['status'][],
): number {
  return tasks.filter((t) => t.type === type && statuses.includes(t.status)).length;
}

export async function fetchMaintenanceDashboardStats(): Promise<MaintenanceDashboardStats> {
  const tasks = await listMaintenanceTasks();
  const active = tasks.filter(
    (t) => t.status === 'scheduled' || t.status === 'in_progress',
  );

  const itemsInMaintenance = new Set(
    active
      .filter((t) => t.type !== 'repair' && t.type !== 'inspection')
      .map((t) => t.inventoryItemId),
  ).size;

  const activeRepairs = countByTypeAndStatus(tasks, 'repair', [
    'scheduled',
    'in_progress',
  ]);

  const pendingInspections = countByTypeAndStatus(tasks, 'inspection', [
    'scheduled',
  ]);

  const blocking = tasks.filter(
    (t) => t.status === 'scheduled' || t.status === 'in_progress',
  );

  let conflicts = 0;
  if (blocking.length > 0) {
    const minStart = blocking.reduce(
      (m, t) => (t.scheduledStart < m ? t.scheduledStart : m),
      blocking[0]!.scheduledStart,
    );
    const maxEnd = blocking.reduce(
      (m, t) => (t.scheduledEnd > m ? t.scheduledEnd : m),
      blocking[0]!.scheduledEnd,
    );
    const reservations = await listReservationsInRange(minStart, maxEnd);
    for (const task of blocking) {
      if (!isBlockingMaintenanceStatus(task.status)) continue;
      const hit = reservations.some(
        (r) =>
          isBlockingReservation(r) &&
          r.inventoryItemIds.includes(task.inventoryItemId) &&
          isDateRangeOverlapping(
            task.scheduledStart,
            task.scheduledEnd,
            r.startDate,
            r.endDate,
          ),
      );
      if (hit) conflicts += 1;
    }
  }

  return {
    itemsInMaintenance,
    activeRepairs,
    pendingInspections,
    maintenanceReservationConflicts: conflicts,
  };
}
